import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Api from '../../../Api';
import { Modal, Input, Button } from '../../../components';
import './style.css';

const STATUS_LABELS = {
  em_execucao: 'Em Execução',
  finalizado: 'Finalizado',
  cancelado: 'Cancelado',
};

export default function ViewExecutionModal({ isOpen, onClose, executionId, companyId, onUpdate }) {
  const [execution, setExecution] = useState(null);
  const [loading, setLoading] = useState(false);

  // Cancel form
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Finish form
  const [showFinishForm, setShowFinishForm] = useState(false);
  const [finishObservation, setFinishObservation] = useState('');
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    if (isOpen && executionId) {
      loadExecution();
      setShowCancelForm(false);
      setShowFinishForm(false);
      setCancelReason('');
      setFinishObservation('');
    }
  }, [isOpen, executionId]);

  async function loadExecution() {
    setLoading(true);
    try {
      const data = await Api.getServiceOrderExecutionById(companyId, executionId);
      setExecution(data);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar execução.');
    } finally {
      setLoading(false);
    }
  }

  async function handleCancel(e) {
    e.preventDefault();
    setCancelling(true);
    try {
      const result = await Api.cancelServiceOrderExecution(companyId, executionId, {
        cancel_reason: cancelReason,
      });
      if (result && !result.error) {
        toast.success('Execução cancelada.');
        onUpdate();
        onClose();
      } else {
        toast.error(result?.error || 'Erro ao cancelar.');
      }
    } catch (error) {
      toast.error('Erro ao cancelar execução.');
    } finally {
      setCancelling(false);
    }
  }

  async function handleFinish(e) {
    e.preventDefault();
    setFinishing(true);
    try {
      const result = await Api.finishServiceOrderExecution(companyId, executionId, {
        observation_after: finishObservation,
        photos: [],
      });
      if (result && !result.error) {
        toast.success('Execução finalizada!');
        onUpdate();
        onClose();
      } else {
        toast.error(result?.error || 'Erro ao finalizar.');
      }
    } catch (error) {
      toast.error('Erro ao finalizar execução.');
    } finally {
      setFinishing(false);
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }

  if (!execution && !loading) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Detalhes da Execução" size="lg">
      <div className="company-form-component">
        <div className="form-body">
          {loading && <p>Carregando...</p>}

          {execution && (
            <>
              <h3 className="section-title" style={{ marginTop: 0 }}>Informações</h3>
              <div className="so-detail-grid">
                <div className="so-detail-item">
                  <span className="so-detail-label">Status</span>
                  <span className={`so-execution-status ${execution.status}`}>
                    {STATUS_LABELS[execution.status] || execution.status}
                  </span>
                </div>
                <div className="so-detail-item">
                  <span className="so-detail-label">Executor</span>
                  <span className="so-detail-value">{execution.executor_name || '-'}</span>
                </div>
                <div className="so-detail-item">
                  <span className="so-detail-label">Início</span>
                  <span className="so-detail-value">{formatDate(execution.started_at)}</span>
                </div>
                {execution.finished_at && (
                  <div className="so-detail-item">
                    <span className="so-detail-label">Finalizado em</span>
                    <span className="so-detail-value">{formatDate(execution.finished_at)}</span>
                  </div>
                )}
                {execution.cancelled_at && (
                  <div className="so-detail-item">
                    <span className="so-detail-label">Cancelado em</span>
                    <span className="so-detail-value">{formatDate(execution.cancelled_at)}</span>
                  </div>
                )}
              </div>

              {execution.observation_before && (
                <>
                  <h3 className="section-title">Observação Inicial</h3>
                  <p className="so-detail-value">{execution.observation_before}</p>
                </>
              )}
              {execution.observation_after && (
                <>
                  <h3 className="section-title">Observação Final</h3>
                  <p className="so-detail-value">{execution.observation_after}</p>
                </>
              )}
              {execution.cancel_reason && (
                <>
                  <h3 className="section-title">Motivo do Cancelamento</h3>
                  <p className="so-detail-value">{execution.cancel_reason}</p>
                </>
              )}

              {/* Photos */}
              {execution.photos && execution.photos.length > 0 && (
                <>
                  <h3 className="section-title">Fotos</h3>
                  <div className="so-photos-grid">
                    {execution.photos.map((photo, idx) => (
                      <img key={idx} src={photo.photo} alt={`Foto ${idx + 1}`} className="so-photo-thumb" />
                    ))}
                  </div>
                </>
              )}

              {/* Actions for em_execucao */}
              {execution.status === 'em_execucao' && !showCancelForm && !showFinishForm && (
                <div className="modal-actions" style={{ borderTop: 'none', paddingLeft: 0, paddingRight: 0 }}>
                  <Button type="button" className="cancel-button" onClick={() => setShowCancelForm(true)}>
                    Cancelar Execução
                  </Button>
                  <Button type="button" onClick={() => setShowFinishForm(true)}>
                    Finalizar Execução
                  </Button>
                </div>
              )}

              {/* Cancel Form */}
              {showCancelForm && (
                <form onSubmit={handleCancel}>
                  <h3 className="section-title">Cancelar Execução</h3>
                  <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <Input
                      id="cancel_reason"
                      label="Motivo do Cancelamento"
                      value={cancelReason}
                      onChange={e => setCancelReason(e.target.value)}
                      multiline
                      rows={3}
                      required
                    />
                  </div>
                  <div className="modal-actions" style={{ borderTop: 'none', paddingLeft: 0, paddingRight: 0 }}>
                    <Button type="button" className="cancel-button" onClick={() => setShowCancelForm(false)}>
                      Voltar
                    </Button>
                    <Button type="submit" disabled={cancelling}>
                      {cancelling ? 'Cancelando...' : 'Confirmar Cancelamento'}
                    </Button>
                  </div>
                </form>
              )}

              {/* Finish Form */}
              {showFinishForm && (
                <form onSubmit={handleFinish}>
                  <h3 className="section-title">Finalizar Execução</h3>
                  <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <Input
                      id="finish_observation"
                      label="Observação Final"
                      value={finishObservation}
                      onChange={e => setFinishObservation(e.target.value)}
                      multiline
                      rows={3}
                    />
                  </div>
                  <div className="modal-actions" style={{ borderTop: 'none', paddingLeft: 0, paddingRight: 0 }}>
                    <Button type="button" className="cancel-button" onClick={() => setShowFinishForm(false)}>
                      Voltar
                    </Button>
                    <Button type="submit" disabled={finishing}>
                      {finishing ? 'Finalizando...' : 'Confirmar Finalização'}
                    </Button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
